import { deleteDoc, doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

import { db, functions } from "../firebase";

const checkHandleAvailableFn = httpsCallable(functions, "checkHandleAvailable");

function cleanUsername(username = "") {
  return username
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._]/g, "");
}

function toJsDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Client-side hold/redirect awareness (server checkHandleAvailable is source of truth). */
function isBlockedByRegistry(data, uid, now = new Date()) {
  if (!data) return false;
  if (data.uid === uid && !data.redirectTo) return false;

  const holdUntil = toJsDate(data.handleHoldUntil);
  if (holdUntil && holdUntil > now) return true;

  if (data.uid && data.uid !== uid && !data.redirectTo && !holdUntil) {
    return true;
  }

  return false;
}

/** Read-only availability check — does not reserve. Enforces 90-day hold. */
export async function checkUsernameAvailable(username, uid) {
  const cleaned = cleanUsername(username);
  if (cleaned.length < 3) return false;

  try {
    const { data } = await checkHandleAvailableFn({ handle: cleaned });
    if (typeof data?.available === "boolean") return data.available;
  } catch {
    /* fall through to Firestore registry */
  }

  const usernameRef = doc(db, "usernames", cleaned);
  const usernameSnap = await getDoc(usernameRef);

  if (!usernameSnap.exists()) return true;
  return !isBlockedByRegistry(usernameSnap.data(), uid);
}

export async function reserveUsername({ username, uid }) {
  if (!username || !uid) {
    throw new Error("Missing username information.");
  }

  const cleaned = cleanUsername(username);

  if (cleaned.length < 3) {
    throw new Error("Username must be at least 3 characters.");
  }

  const available = await checkUsernameAvailable(cleaned, uid);
  if (!available) {
    throw new Error("Username is already taken or on hold.");
  }

  const usernameRef = doc(db, "usernames", cleaned);
  await setDoc(usernameRef, {
    uid,
    username: cleaned,
    status: "active",
    updatedAt: serverTimestamp(),
  });

  return cleaned;
}

export async function releaseUsername(username) {
  const cleaned = cleanUsername(username);

  if (!cleaned) return;

  await deleteDoc(doc(db, "usernames", cleaned));
}
